import type { ReactNode } from 'react';
import { createContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IInsuranceHistory } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization } from '@common/enums';
import { hasOneAuthorization, useAbortableFetch } from '@frontend/utils';

export interface IPatientActiveInsuranceContext {
    activeInsurance: IInsuranceHistory | undefined;
}

interface IPatientActiveInsuranceContextProps {
    children: ReactNode;
    patientId: string;
}

export const PatientActiveInsuranceContext = createContext<IPatientActiveInsuranceContext>({
    activeInsurance: undefined,
});

export function PatientActiveInsuranceContextProvider(props: IPatientActiveInsuranceContextProps): JSX.Element {
    const abortableFetch = useAbortableFetch();
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const [insuranceHistory, setInsuranceHistory] = useState<IInsuranceHistory | undefined>();

    useEffect(() => {
        if (props.patientId && (hasOneAuthorization(ManagerAuthorization.patientInsurance) || !isManager)) {
            loadInsuranceHistory(props.patientId);
        }
    }, [props.patientId]);

    return (
        <PatientActiveInsuranceContext.Provider value={{ activeInsurance: insuranceHistory }}>
            {props.children}
        </PatientActiveInsuranceContext.Provider>
    );

    async function loadInsuranceHistory(patientId: string) {
        const url = `/api/patients/${patientId}/insuranceHistory?isActive=true`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setInsuranceHistory(result.json.items?.[0]);
    }
}
